/**
 * What the current user may change on the task page. These mirror the server's rules in
 * TaskAuthorizationUtility, which is what actually enforces them; the page only uses them to
 * enable or disable controls.
 *
 * Every function takes the same `context`:
 *   isSuperAdmin, isAdmin  - from the token (isAdmin is also true for super admins)
 *   userId                 - the token's nameid claim, a string
 *   projectUserXRefs       - [{ projectId, permissionLevel }] from the token
 *   taskId                 - the route parameter; empty or '0' for a task being created
 *   task                   - the task as currently loaded/edited
 */

export function isNewTask(taskId) {
    return !taskId || parseInt(taskId) === 0;
}

// Only super and org admins create (or copy) tasks. The server additionally requires an org admin
// to hold a permission row for the chosen project. Audit checklists create tasks through their own
// path and are not gated by this.
export function canCreateTasks({ isSuperAdmin, isAdmin }) {
    return Boolean(isSuperAdmin || isAdmin);
}

export function canModifyFields(context) {
    const { isSuperAdmin, isAdmin, userId, projectUserXRefs, taskId, task } = context;

    // A new task opens on a default project the user may hold no rights on, so gate it on being
    // able to create tasks at all; the server checks the project actually chosen.
    if (isNewTask(taskId)) {
        return canCreateTasks(context);
    }

    const projectPermission = projectUserXRefs?.find((permission) => permission.projectId === task?.projectId);

    return Boolean(isSuperAdmin ||
        (task?.reportedUserId === userId) ||
        // Org admins may edit any task in a project they hold a permission row for, at any level,
        // as in TaskAuthorizationUtility.IsAuthorizedForTask.
        (isAdmin && projectPermission != null) ||
        (projectPermission?.permissionLevel > 1));
}

// A task's built-in fields (everything but its custom columns) are admin-only to change once the
// task exists; whoever creates a task fills them in. Mirrors the TaskAuthorizationUtility.IsTaskAdmin
// check in TaskService.SaveTaskInfoAsync, which rejects a save that changes any of them.
export function canModifyBuiltInFields(context) {
    if (!canModifyFields(context)) return false;
    if (context.isSuperAdmin || context.isAdmin) return true;
    return isNewTask(context.taskId);
}

// Custom columns are admin-only to change. A column flagged editableByAssignee is also open to
// whoever the task is currently assigned to, which is the only way a non-admin edits one.
// Mirrors TaskAuthorizationUtility.CanEditCustomColumn.
export function canModifyCustomColumn(context, customColumn) {
    const { isSuperAdmin, isAdmin, userId, taskId, task } = context;

    if (!canModifyFields(context)) return false;
    if (isSuperAdmin || isAdmin) return true;
    // A task being created has no assignee yet; its creator fills the columns in.
    if (isNewTask(taskId)) return true;

    return Boolean(customColumn?.customColumn?.editableByAssignee)
        && task?.assignedToUserId != null
        // userId comes from the JWT nameid claim, which is a string.
        && task.assignedToUserId === Number(userId);
}
